/**
 * Validación del formulario contra la metadata.
 *
 * Es una comodidad para el usuario, **no** una garantía: la validación de verdad
 * la hace el backend con `full_clean()`, y sus errores se muestran campo por
 * campo con el `fields` de `ApiError`. Acá solo se evita el viaje de ida y
 * vuelta para los errores obvios.
 *
 * Por eso las reglas son las que se pueden deducir de la metadata sin inventar:
 * obligatorio, largo máximo, forma de número y de email. Nada de reglas de
 * negocio: esas viven en el modelo, que es donde se pueden hacer cumplir.
 */

import { isEmpty } from '@/components/fields/format';
import type { FieldMeta, FieldValue, ModelSpec } from '@/lib/types';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateField(meta: FieldMeta, valor: FieldValue): string | null {
  if (meta.readonly) return null;

  if (meta.required && isEmpty(valor)) {
    return 'Este dato es obligatorio.';
  }

  if (isEmpty(valor)) return null;

  if (typeof valor === 'string') {
    if (meta.max_length !== undefined && valor.length > meta.max_length) {
      return `No puede superar los ${meta.max_length} caracteres.`;
    }
    if (meta.type === 'email' && !EMAIL.test(valor.trim())) {
      return 'Escribí un email válido, con arroba y dominio.';
    }
    if (meta.type === 'url') {
      try {
        // Solo interesa si construye o no: el resultado se descarta.
        new URL(valor);
      } catch {
        return 'Escribí una dirección completa, empezando por https://';
      }
    }
  }

  if (meta.type === 'integer' || meta.type === 'float' || meta.type === 'decimal') {
    const numero = typeof valor === 'number' ? valor : Number(valor);
    if (!Number.isFinite(numero)) return 'Tiene que ser un número.';
    if (meta.type === 'integer' && !Number.isInteger(numero)) return 'Tiene que ser un número entero.';

    const digits = meta.digits;
    if (Array.isArray(digits) && typeof digits[0] === 'number' && typeof digits[1] === 'number') {
      const enteros = digits[0] - digits[1];
      const limite = 10 ** enteros;
      if (Math.abs(numero) >= limite) {
        return `El valor es demasiado grande: admite hasta ${enteros} dígitos enteros.`;
      }
    }
  }

  return null;
}

/** Valida el formulario completo. Devuelve `{campo: mensaje}` solo con los que fallan. */
export function validateRecord(
  spec: ModelSpec,
  valores: Record<string, FieldValue>,
  campos: string[],
): Record<string, string> {
  const errores: Record<string, string> = {};
  for (const nombre of campos) {
    const meta = spec.fields[nombre];
    if (meta === undefined) continue;
    // Un one2many no se escribe desde el padre: no tiene sentido validarlo acá.
    if (meta.type === 'one2many') continue;
    const mensaje = validateField(meta, valores[nombre] ?? null);
    if (mensaje !== null) errores[nombre] = mensaje;
  }
  return errores;
}
